import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	ConfigurationType,
	OAuth,
	OAuthInterface,
	OAuthProvider,
	OAuthProviderLogout,
	OAuthProviderLogoutInterface,
	OAuthProviderName,
	OAuthProviders, OAuthProvidersInterface,
	Server
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import Icon from "../../../shared/components/Icon";
import OAuthDialogFields from "../field/OAuthDialogFields";
import {AccordionTab} from "primereact/accordion";
import {InputText} from "primereact/inputtext";
import AddOAuthProviderDialog from "./AddOAuthProviderDialog";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {Menu} from "primereact/menu";
import Info, {InfoType} from "../../../shared/components/Info";

export interface OAuthDialogProperties extends AdminDialogProperties<OAuth> {
}

export interface OAuthDialogState extends AdminDialogState<OAuth> {
	showAddDialog: boolean;
	addParameter?: OAuthProvider;
	type: OAuthProviderName;
}

export default abstract class OAuthDialog extends AdminDialog<OAuth, OAuthDialogProperties, OAuthDialogState> {
	constructor(props: Readonly<OAuthDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			showAddDialog: false,
			type: OAuthProviderName.Generic
		};
	}

	protected renderProvider(provider: OAuthProvider, key: number, options: { logout: boolean }): ReactNode {
		let discoveryMenu: RefObject<Menu> = createRef<Menu>();
		let discoveryUrl: string = FunctionHelper.applyValue(
			provider.discoveryUrl, OAuthProvider.getDiscoveryUrlDefault()
		);
		let logoutMenu: RefObject<Menu> = createRef<Menu>();
		let logoutUrl: string = FunctionHelper.applyValue(provider.logout?.url, OAuthProviderLogout.getUrlDefault());

		return (
			<AccordionTab key={key} headerTemplate={
				<>
					{FunctionHelper.applyValue(provider.displayName, provider.name, OAuthProvider.getNameDefault())}
					<div className="buttons">
						<Button icon="icon icon-delete small" onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();

							let parameter: OAuth | undefined = this.state.parameter;
							let providers: Array<OAuthProvider> = FunctionHelper.applyValue(
								parameter?.providers?.provider, OAuthProviders.getProviderDefault()
							);

							for (let [index, currentProvider] of providers.entries()) {
								if (currentProvider.id === provider.id) {
									providers.splice(index, 1);
									parameter!.providers!.provider = providers;

									this.setState({
										parameter: this.state.parameter,
										hasChanges: this.hasChanges()
									});
									break;
								}
							}
						}}/>
					</div>
				</>
			}>
				<div className="p-grid">
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-name-" + key}>
							<Translate {...this.props} data="oauth.name" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getDisplayNameDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-name-" + key}
									   value={FunctionHelper.applyValue(
										   provider.displayName, OAuthProvider.getDisplayNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.DISPLAY_NAME, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-client-id-" + key}>
							<Translate {...this.props} data="oauth.client_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getClientIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-client-id-" + key}
									   value={FunctionHelper.applyValue(
										   provider.clientId, OAuthProvider.getClientIdDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.CLIENT_ID, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-application-id-" + key}>
							<Translate {...this.props} data="oauth.application_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getApplicationIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-application-id-" + key}
									   value={FunctionHelper.applyValue(
										   provider.applicationId, OAuthProvider.getApplicationIdDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.APPLICATION_ID, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-tenant-id-" + key}>
							<Translate {...this.props} data="oauth.tenant_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getTenantIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-tenant-id-" + key}
									   value={FunctionHelper.applyValue(
										   provider.tenantId, OAuthProvider.getTenantIdDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.TENANT_ID, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-scope-" + key}>
							<Translate {...this.props} data="oauth.scope" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getScopeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-scope-" + key}
									   value={FunctionHelper.applyValue(
										   provider.scope, OAuthProvider.getScopeDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.SCOPE, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-discovery-url-" + key}>
							<Translate {...this.props} data="oauth.discovery_url" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getDiscoveryUrlDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<div className="p-inputgroup">
								<InputText id="control-discovery-url" value={discoveryUrl}
										   onInput={(e) => this.onChange(
											   OAuthDialogFields.DISCOVERY_URL, e.currentTarget.value, provider.id
										   )}/>
								<Button icon="icon icon-angle-down" aria-controls={"control-discovery-url-menu"}
										onClick={(event) => discoveryMenu.current!.toggle(event)}
										className="p-inputgroup-addon" aria-haspopup={true}/>
								<Menu popup={true} ref={discoveryMenu} id="control-discovery-url-menu"
									  appendTo={document.getElementById("root")}
									  model={[
										  {
											  label: Translate.value(
												  "oauth.vars.client_id", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{clientId}",
												  provider.id
											  )
										  },
										  {
											  label: Translate.value("oauth.vars.app_id", {ns: "dialog"}),
											  command: (e) => this.onChange(
												  OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{appId}",
												  provider.id
											  )
										  },
										  {
											  label: Translate.value(
												  "oauth.vars.tenant_id", {ns: "dialog"}
											  ),
											  command: (e) => this.onChange(
												  OAuthDialogFields.DISCOVERY_URL, discoveryUrl + "{tenantId}",
												  provider.id
											  )
										  }
									  ]}/>
							</div>
						</div>
					</div>
					{options.logout &&
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-4" htmlFor={"control-logout-" + key}>
								<Translate {...this.props} data="oauth.logout" namespace="dialog"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={OAuthProviderLogout.getUrlDescription()}/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className="p-inputgroup">
									<InputText id="control-logout" value={logoutUrl}
											   onInput={(e) => this.onChange(
												   OAuthDialogFields.LOGOUT, e.currentTarget.value, provider.id
											   )}/>
									<Button icon="icon icon-angle-down" aria-controls={"control-logout-menu"}
											onClick={(event) => logoutMenu.current!.toggle(event)}
											className="p-inputgroup-addon" aria-haspopup={true}/>
									<Menu id="control-logout-menu" popup={true} ref={logoutMenu}
										  appendTo={document.getElementById("root")}
										  model={[
											  {
												  label: Translate.value(
													  "oauth.vars.redirect_url", {ns: "dialog"}
												  ),
												  command: (e) => this.onChange(
													  OAuthDialogFields.LOGOUT, logoutUrl + "{redirectUrl}",
													  provider.id
												  )
											  },
											  {
												  label: Translate.value(
													  "oauth.vars.client_id", {ns: "dialog"}
												  ),
												  command: (e) => this.onChange(
													  OAuthDialogFields.LOGOUT, logoutUrl + "{clientId}",
													  provider.id
												  )
											  },
											  {
												  label: Translate.value(
													  "oauth.vars.app_id", {ns: "dialog"}
												  ),
												  command: (e) => this.onChange(
													  OAuthDialogFields.LOGOUT, logoutUrl + "{appId}",
													  provider.id
												  )
											  }
										  ]}/>
								</div>
							</div>
						</div>
					}
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-claim-client-id-" + key}>
							<Translate {...this.props} data="oauth.claim_client_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getClaimClientIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-claim-client-id-" + key}
									   value={FunctionHelper.applyValue(
										   provider.claimClientId, OAuthProvider.getClaimClientIdDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.CLAIM_CLIENT_ID, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-claim-session-id-" + key}>
							<Translate {...this.props} data="oauth.claim_session_id" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getClaimSessionIdDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-claim-session-id-" + key}
									   value={FunctionHelper.applyValue(
										   provider.claimSessionId, OAuthProvider.getClaimSessionIdDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.CLAIM_SESSION_ID, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-claim-roles-" + key}>
							<Translate {...this.props} data="oauth.claim_roles" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getClaimRolesDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-claim-roles-" + key}
									   value={FunctionHelper.applyValue(
										   provider.claimRoles, OAuthProvider.getClaimRolesDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.CLAIM_ROLES, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-role-admin-" + key}>
							<Translate {...this.props} data="oauth.role_admin" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getRoleNameAdminDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-role-admin-" + key}
									   value={FunctionHelper.applyValue(
										   provider.roleNameAdmin, OAuthProvider.getRoleNameAdminDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.ROLE_ADMIN, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor={"control-role-user-" + key}>
							<Translate {...this.props} data="oauth.role_user" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={OAuthProvider.getRoleNameUserDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<InputText id={"control-role-user-" + key}
									   value={FunctionHelper.applyValue(
										   provider.roleNameUser, OAuthProvider.getRoleNameUserDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OAuthDialogFields.ROLE_USER, e.currentTarget.value, provider.id
									   )}/>
						</div>
					</div>
				</div>
			</AccordionTab>
		);
	}

	protected abstract renderContents(): ReactNode;

	protected renderAddDialog() {
		return (
			<AddOAuthProviderDialog {...this.props} visible={this.state.showAddDialog} logout={true}
									hide={() => this.setState({showAddDialog: false})}
									parameter={this.state.addParameter} providerType={this.state.type}
									onSubmit={(result: OAuthProvider) => {
										let parameter: OAuth | undefined = this.state.parameter;

										if (!FunctionHelper.exists(parameter)) {
											parameter = OAuth.fromJson({
												providers: {
													provider: [] as Array<OAuthProvider>
												}
											} as OAuthInterface);
										}

										if (!FunctionHelper.exists(parameter?.providers)) {
											parameter!.providers = OAuthProviders.fromJson({
												provider: [] as Array<OAuthProvider>
											} as OAuthProvidersInterface);
										}

										if (!FunctionHelper.exists(parameter?.providers?.provider)) {
											parameter!.providers!.provider = OAuthProviders.getProviderDefault();
										}

										parameter!.providers!.provider!.push(result);

										this.setState({
											parameter: parameter
										}, () => {
											this.setState({
												hasChanges: this.hasChanges()
											});
										});
									}}/>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["oauth-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					{this.renderContents()}
					{this.renderAddDialog()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-oauth"}/>
					<Translate {...this.props} data="oauth.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: OAuthDialogFields, value: any, providerId?: string): void {
		let parameter: OAuth | undefined = this.state.parameter;
		let changedProvider: OAuthProvider | undefined;

		for (let provider of FunctionHelper.applyValue(
			parameter?.providers?.provider, OAuthProviders.getProviderDefault()
		)) {
			if (provider.id === providerId) {
				changedProvider = provider;
				break;
			}
		}

		if (!FunctionHelper.exists(changedProvider)) {
			return;
		}

		switch (field) {
			case OAuthDialogFields.DISPLAY_NAME:
				changedProvider!.displayName = value;
				break;
			case OAuthDialogFields.CLIENT_ID:
				changedProvider!.clientId = value;
				break;
			case OAuthDialogFields.APPLICATION_ID:
				changedProvider!.applicationId = value;
				break;
			case OAuthDialogFields.TENANT_ID:
				changedProvider!.tenantId = value;
				break;
			case OAuthDialogFields.SCOPE:
				changedProvider!.scope = value;
				break;
			case OAuthDialogFields.DISCOVERY_URL:
				changedProvider!.discoveryUrl = value;
				break;
			case OAuthDialogFields.LOGOUT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					changedProvider!.logout = undefined;
				} else {
					changedProvider!.logout = OAuthProviderLogout.fromJson({
						url: value
					} as OAuthProviderLogoutInterface);
				}
				break;
			case OAuthDialogFields.CLAIM_CLIENT_ID:
				changedProvider!.claimClientId = value;
				break;
			case OAuthDialogFields.CLAIM_SESSION_ID:
				changedProvider!.claimSessionId = value;
				break;
			case OAuthDialogFields.CLAIM_ROLES:
				changedProvider!.claimRoles = value;
				break;
			case OAuthDialogFields.ROLE_ADMIN:
				changedProvider!.roleNameAdmin = value;
				break;
			case OAuthDialogFields.ROLE_USER:
				changedProvider!.roleNameUser = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let serverConfiguration: Server = (
			await administration.getConfiguration(ConfigurationType.Server) as Server
		).clone();
		serverConfiguration.oauth = this.state.parameter;

		try {
			await administration.updateConfiguration(ConfigurationType.Server, serverConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.oauth", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}
}